<template>
	<div class="amadeus">
		<template v-if="!apiKey">
			<h2>OpenAI API Key</h2>
			<p>Bitte in den Einstellungen einen API-Key eintragen.</p>
			<router-link to="/settings">Einstellungen</router-link>
		</template>
		<template v-else>
			<h1 class="title">Amadeus</h1>
			<p class="subtitle">Ask Me Anything Deus</p>
			<p v-if="error" class="err">{{ error }}</p>
			<div
				class="button-wrap"
				:class="{ 'ring-on': connected }"
			>
				<svg
					class="ring-svg"
					viewBox="0 0 100 100"
					aria-hidden="true"
				>
					<circle
						class="ring-track"
						cx="50"
						cy="50"
						:r="RING_R"
						fill="none"
					/>
					<circle
						class="ring-progress"
						:class="{ warn: ringWarn }"
						cx="50"
						cy="50"
						:r="RING_R"
						fill="none"
						:stroke-dasharray="ringLen"
						:stroke-dashoffset="ringOffset"
						transform="rotate(-90 50 50)"
					/>
				</svg>
				<button
					type="button"
					class="pulse"
					:disabled="busy"
					@click="onMainClick"
				>
					{{ buttonLabel }}
				</button>
			</div>
			<p class="hint">{{ hint }}</p>
		</template>
	</div>
</template>

<script setup>
import { computed, onUnmounted, ref } from 'vue'
import { useSettingsStore } from '../stores/settingsStore'

const SESSION_SECONDS = 60
const RING_R = 46
const ringLen = 2 * Math.PI * RING_R
const REALTIME_CALLS = 'https://api.openai.com/v1/realtime/calls'
const AMADEUS_INSTRUCTIONS = `Du bist Amadeus (Ask Me Anything Deus). Du sprichst mit Kindern auf Deutsch.
Sobald das Gespräch beginnt, begrüße freundlich mit etwas wie: "Hallo, wie kann ich dir helfen?"
Hör zu, was das Kind braucht. Wenn es eine Frage hat, beantworte sie altersgerecht und einfach.
Biete am Ende an, zum Thema noch mehr zu erklären, wenn es möchte.
Wenn das Kind nur plaudern will, bleib warm und kurzweilig.`

const settings = useSettingsStore()
const apiKey = computed(() => settings.openAIKey?.trim())
const busy = ref(false)
const error = ref('')
const connected = ref(false)
const remaining = ref(0)
let timerEndsAt = 0
let rafId = null
let pc = null
let dc = null
let mediaStream = null
let remoteAudio = null
let responseStarted = false

const ringOffset = computed(() => ringLen * (1 - remaining.value))
const ringWarn = computed(() => connected.value && remaining.value > 0 && remaining.value <= 10 / SESSION_SECONDS)

const buttonLabel = computed(() => {
	if (busy.value) return '…'
	if (connected.value) return 'Zeit verlängern'
	return 'Gespräch starten'
})

const hint = computed(() => {
	if (!connected.value) {
		return 'Tippen startet das Gespräch (WebRTC, Echoausblendung). Der Ring zeigt die Zeit — erneut tippen füllt ihn.'
	}
	return 'Ring leer? Gespräch endet. Nochmal tippen füllt den Ring und hält weiter.'
})

function clearTimer() {
	if (rafId != null) {
		cancelAnimationFrame(rafId)
		rafId = null
	}
}

function ringTick() {
	const ms = timerEndsAt - Date.now()
	if (ms <= 0) {
		remaining.value = 0
		clearTimer()
		teardown()
		return
	}
	remaining.value = ms / (SESSION_SECONDS * 1000)
	rafId = requestAnimationFrame(ringTick)
}

function armTimer() {
	timerEndsAt = Date.now() + SESSION_SECONDS * 1000
	remaining.value = 1
	clearTimer()
	rafId = requestAnimationFrame(ringTick)
}

function stopRemoteAudio() {
	if (remoteAudio) {
		try {
			remoteAudio.pause()
			remoteAudio.srcObject = null
		} catch { /* teardown */ }
		remoteAudio = null
	}
}

function onDcMessage(ev) {
	if (!connected.value || !dc || dc.readyState !== 'open') return
	let msg
	try {
		msg = JSON.parse(ev.data)
	} catch {
		return
	}
	if (msg.type === 'error') error.value = msg.message || String(msg.error || msg)
	if (msg.type === 'session.created') sendInitialResponse()
}

function sendInitialResponse() {
	if (responseStarted || !dc || dc.readyState !== 'open') return
	responseStarted = true
	dc.send(JSON.stringify({ type: 'response.create' }))
}

function teardown() {
	clearTimer()
	remaining.value = 0
	connected.value = false
	responseStarted = false
	stopRemoteAudio()
	if (dc) {
		try {
			dc.onmessage = null
			dc.close()
		} catch { /* teardown */ }
		dc = null
	}
	if (pc) {
		try {
			pc.ontrack = null
			pc.close()
		} catch { /* teardown */ }
		pc = null
	}
	if (mediaStream) {
		mediaStream.getTracks().forEach((t) => t.stop())
		mediaStream = null
	}
}

async function ensureRealtimeSession() {
	const body = {
		expires_after: { anchor: 'created_at', seconds: 120 },
		session: {
			type: 'realtime',
			model: 'gpt-realtime-mini',
			instructions: AMADEUS_INSTRUCTIONS,
			audio: {
				input: {
					format: { type: 'audio/pcm', rate: 24000 },
					turn_detection: { type: 'server_vad' },
					noise_reduction: { type: 'near_field' },
				},
				output: {
					format: { type: 'audio/pcm', rate: 24000 },
					voice: 'marin',
				},
			},
		},
	}
	const r = await fetch('https://api.openai.com/v1/realtime/client_secrets', {
		method: 'POST',
		headers: {
			Authorization: `Bearer ${apiKey.value}`,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(body),
	})
	if (!r.ok) {
		const t = await r.text()
		throw new Error(t.slice(0, 200) || r.statusText)
	}
	const data = await r.json()
	const secret = data.value || data.client_secret?.value
	if (!secret) throw new Error('Kein Client-Secret')
	return secret
}

async function connect() {
	error.value = ''
	busy.value = true
	try {
		const secret = await ensureRealtimeSession()
		pc = new RTCPeerConnection()
		remoteAudio = document.createElement('audio')
		remoteAudio.setAttribute('playsinline', 'true')
		remoteAudio.autoplay = true
		pc.ontrack = (e) => {
			if (remoteAudio) remoteAudio.srcObject = e.streams[0]
		}
		mediaStream = await navigator.mediaDevices.getUserMedia({
			audio: {
				echoCancellation: true,
				noiseSuppression: true,
				autoGainControl: true,
			},
		})
		pc.addTrack(mediaStream.getTracks()[0])
		dc = pc.createDataChannel('oai-events')
		dc.onmessage = onDcMessage
		dc.onopen = () => sendInitialResponse()
		const offer = await pc.createOffer()
		await pc.setLocalDescription(offer)
		let sdpRes = await fetch(REALTIME_CALLS, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${secret}`,
				'Content-Type': 'application/sdp',
			},
			body: offer.sdp,
		})
		if (!sdpRes.ok) {
			const fd = new FormData()
			fd.set('sdp', offer.sdp)
			fd.set(
				'session',
				JSON.stringify({
					type: 'realtime',
					model: 'gpt-realtime-mini',
					instructions: AMADEUS_INSTRUCTIONS,
					audio: {
						output: { voice: 'marin' },
					},
				})
			)
			sdpRes = await fetch(REALTIME_CALLS, {
				method: 'POST',
				headers: { Authorization: `Bearer ${secret}` },
				body: fd,
			})
		}
		if (!sdpRes.ok) {
			const t = await sdpRes.text()
			throw new Error(t.slice(0, 200) || sdpRes.statusText)
		}
		const answerSdp = await sdpRes.text()
		await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp })
		connected.value = true
		armTimer()
		sendInitialResponse()
	} catch (e) {
		error.value = e.message || String(e)
		teardown()
	} finally {
		busy.value = false
	}
}

async function onMainClick() {
	if (!apiKey.value) return
	if (connected.value) {
		armTimer()
		return
	}
	await connect()
}

onUnmounted(() => {
	teardown()
})
</script>

<style scoped>
.amadeus {
	padding: 2rem;
	text-align: center;
	max-width: 28rem;
	margin: 0 auto;
}
.title {
	margin: 0;
	font-size: 1.75rem;
	color: var(--font-color, #421);
}
.subtitle {
	margin: 0.25rem 0 1rem;
	opacity: 0.75;
	font-size: 0.95rem;
}
.err {
	color: #b00;
	font-size: 0.9rem;
}
.button-wrap {
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	margin: 1rem 0;
	width: min(78vw, 260px);
	height: min(78vw, 260px);
	vertical-align: middle;
}
.ring-svg {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
	opacity: 0;
	transition: opacity 0.25s;
}
.button-wrap.ring-on .ring-svg {
	opacity: 1;
}
.ring-track {
	stroke: rgba(0, 0, 0, 0.08);
	stroke-width: 5;
}
.ring-progress {
	stroke: var(--orange, #e94);
	stroke-width: 5;
	stroke-linecap: round;
	transition: stroke 0.3s;
}
.ring-progress.warn {
	stroke: #c33;
}
.pulse {
	width: min(70vw, 220px);
	height: min(70vw, 220px);
	border-radius: 50%;
	border: none;
	font-size: 1.1rem;
	font-weight: bold;
	color: #fff;
	background: linear-gradient(145deg, var(--orange, #e94), var(--dark-orange, #c60));
	box-shadow: 0 6px 20px var(--shadow, rgba(80, 40, 0, 0.35));
	cursor: pointer;
	touch-action: manipulation;
}
.pulse:disabled {
	opacity: 0.6;
	cursor: wait;
}
.hint {
	margin-top: 0.75rem;
	font-size: 0.85rem;
	opacity: 0.8;
	line-height: 1.35;
}
</style>
